import { useState, useEffect, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import i18n, { isLanguageHosted, normalize } from "@/i18n";
import {
  LanguageContext,
  type Language,
  type LanguageContextType,
} from "@/shared/lib/language";

const LANGUAGE_KEY = "storemandu-language";

function readStoredLanguage(): Language | null {
  try {
    const stored = localStorage.getItem(LANGUAGE_KEY);
    return stored === "en" || stored === "ne" || stored === "hi" ? stored : null;
  } catch {
    // localStorage may be unavailable in some embedded contexts.
    return null;
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n: i18nInstance } = useTranslation();
  // Read once: whether a host launched this page does not change while it runs.
  const [hosted] = useState(isLanguageHosted);

  // Boot precedence.
  //
  // HOSTED (iframe or WebView): the host owns the language, exactly as it owns
  // the theme (ThemeProvider). Its boot `?lng=` is what i18n.ts resolved, and
  // studio:context (HostContextBridge) is the live value. This origin's stored
  // language is never read or re-asserted here: doing so flipped the language
  // back to a stale "en" on every remount and, with the host answering each
  // re-announced studio:ready, froze the dashboard tab (prod, 12 Oct).
  //
  // STANDALONE, unchanged: the URL (?lng=ne), then the persisted choice
  // ("storemandu-language", which predates i18next's own key), then the
  // browser, then "en".
  const [language, setLanguageState] = useState<Language>(() => {
    const resolved = normalize(i18n.language) as Language;
    if (hosted) return resolved;
    return readStoredLanguage() ?? resolved;
  });

  // Follow i18next, whoever changed it (the host's studio:context goes straight
  // to i18n.changeLanguage), so the context never reports a stale language.
  useEffect(() => {
    const onChanged = (lng: string) =>
      setLanguageState(normalize(lng) as Language);
    i18nInstance.on("languageChanged", onChanged);
    return () => i18nInstance.off("languageChanged", onChanged);
  }, [i18nInstance]);

  useEffect(() => {
    // Standalone only: bring i18next to the persisted choice. Hosted, this
    // provider never changes the language on its own.
    if (hosted) return;
    if (normalize(i18nInstance.language) !== language) {
      void i18nInstance.changeLanguage(language);
    }
  }, [hosted, language, i18nInstance]);

  useEffect(() => {
    // Update HTML lang attribute for accessibility
    document.documentElement.lang = language;
    // The host's locale is not this origin's preference: never saved hosted.
    if (hosted) return;
    try {
      localStorage.setItem(LANGUAGE_KEY, language);
    } catch {
      // ignore
    }
  }, [hosted, language]);

  const setLanguage = async (lang: Language) => {
    setLanguageState(lang);
    await i18nInstance.changeLanguage(lang);
  };

  // Use i18n's translation function
  const t = (key: string, options?: any): string => {
    const result = i18nInstance.t(key, options);
    return typeof result === "string" ? result : String(result);
  };

  const value: LanguageContextType = { language, setLanguage, t, i18n: i18nInstance };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}
